import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Bed, Bath, Square, MapPin, Play, ArrowRight, Home, Lock, UserPlus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { ddfAPI, propertiesAPI } from '../../services/api';
import { FEATURED_LISTINGS } from '../../data/mockData';

// Cache key for localStorage
const FEATURED_CACHE_KEY = 'conti_featured_listings_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const FeaturedListings = () => {
  const { user, isFavorite, addFavorite, removeFavorite } = useAuth();
  const navigate = useNavigate();
  const [showSignupWall, setShowSignupWall] = useState(false);
  // Start with cached data or mock data for instant display
  const [listings, setListings] = useState(() => {
    try {
      const cached = localStorage.getItem(FEATURED_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION && data?.length > 0) {
          return data;
        }
      }
    } catch (e) {}
    return FEATURED_LISTINGS.slice(0, 6); // Fallback to mock for instant display
  });
  const [loading, setLoading] = useState(false); // Start as not loading since we have data
  const [useLiveData, setUseLiveData] = useState(false);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        // Fetch featured $1M+ residential properties from Brantford area
        const luxuryData = await ddfAPI.getLuxury();
        if (luxuryData && luxuryData.length > 0) {
          const newListings = luxuryData.slice(0, 6);
          setListings(newListings);
          setUseLiveData(true);
          // Cache the results
          localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify({
            data: newListings,
            timestamp: Date.now()
          }));
        } else {
          // Fallback to regular featured if no luxury found
          const ddfData = await ddfAPI.getFeatured();
          if (ddfData && ddfData.length > 0) {
            const pricedListings = ddfData.filter(p => p.price > 0);
            if (pricedListings.length >= 3) {
              const newListings = pricedListings.slice(0, 6);
              setListings(newListings);
              setUseLiveData(true);
              localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify({
                data: newListings,
                timestamp: Date.now()
              }));
            }
          }
        }
      } catch (err) {
        console.log('Using cached/mock data for featured listings');
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  const handleFavorite = (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      return;
    }
    if (isFavorite(propertyId)) {
      removeFavorite(propertyId);
    } else {
      addFavorite(propertyId);
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-end mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Home className="text-red-600" size={28} />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Featured <span className="text-red-600">Listings</span>
              </h2>
              <Badge className="bg-amber-500 text-white text-sm px-3 py-1">$500K+</Badge>
            </div>
            <p className="text-gray-600">
              {useLiveData ? 'Quality homes in Brantford & Brant County' : 'Exceptional homes for discerning buyers'}
            </p>
          </div>
          <Link 
            to="/search?minPrice=500000&keyword=Brantford" 
            className="hidden md:flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors"
          >
            View All $500K+
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* Listings - always show data (cached or mock), no loading skeleton needed */}
        {listings.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((property) => {
            // Format price display
            const formatPrice = () => {
              if (property.priceLabel === 'contact' || property.price === 0) {
                return 'Contact for Price';
              }
              if (property.priceLabel === 'lease') {
                return `$${property.price.toLocaleString()}/mo`;
              }
              return `$${property.price.toLocaleString()}`;
            };

            // Handle property click - show signup wall if not logged in
            const handlePropertyClick = (e) => {
              if (!user) {
                e.preventDefault();
                setShowSignupWall(true);
              }
            };

            return (
            <Link key={property.id} to={`/property/${property.id}`} onClick={handlePropertyClick}>
              <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 shadow-md">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={property.image} 
                    alt={property.address}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Status Badge */}
                  <Badge className={`absolute top-4 left-4 ${property.priceLabel === 'lease' ? 'bg-blue-600' : 'bg-green-600'}`}>
                    {property.priceLabel === 'lease' ? 'For Lease' : property.status}
                  </Badge>
                  
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => handleFavorite(e, property.id)}
                    className="absolute top-4 right-4 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
                  >
                    <Heart 
                      size={20} 
                      className={isFavorite(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}
                    />
                  </button>

                  {/* Lock overlay for non-logged in users */}
                  {!user && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-white/95 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                        <Lock size={16} className="text-red-600" />
                        <span className="text-sm font-medium text-gray-800">Sign up to view</span>
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className="absolute bottom-4 left-4">
                    <div className="text-white text-2xl font-bold drop-shadow-lg">
                      {formatPrice()}
                    </div>
                  </div>
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin size={16} className="text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                        {property.address}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {property.city}, {property.province} {property.postalCode}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-gray-600 text-sm mb-3">
                    <div className="flex items-center gap-1">
                      <Bed size={16} />
                      <span>{property.beds} Beds</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath size={16} />
                      <span>{property.baths} Baths</span>
                    </div>
                    {property.sqft > 0 && (
                      <div className="flex items-center gap-1">
                        <Square size={16} />
                        <span>{property.sqft.toLocaleString()} sqft</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                    <span>{property.propertyType}</span>
                    <span>MLS® {property.mlsNumber}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )})}
          </div>
        )}

        <div className="text-center mt-8 md:hidden">
          <Link to="/search?featured=true">
            <Button className="bg-red-600 hover:bg-red-700">
              View All Listings
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Signup Wall Dialog */}
      <Dialog open={showSignupWall} onOpenChange={setShowSignupWall}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-4">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Create a Free Account
            </h3>
            <p className="text-gray-600 mb-6">
              Sign up for free to view property photos, details, save your favorites, and get notified about new listings.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                onClick={() => navigate('/register')}
              >
                <UserPlus size={18} className="mr-2" />
                Create Free Account
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Already have an account? Sign In
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Free forever. No credit card required.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FeaturedListings;
